<template>
  <div class="font">
    <div>
      <h3>{{ nurseryName }}</h3>
      <h3>{{ yearMonth }}</h3>
      <h2>Petty Cash Book</h2>
    </div>

    <!-- account titles -->
    <div class="pt-6 font">
      <h5>Account Titles</h5>
      <v-table class="pt-1 font" density="compact">
        <template v-slot:default>
          <thead>
            <tr>
              <th style="width: 34%">Account</th>
              <th style="width: 33%">Income Total</th>
              <th style="width: 33%">Expense Total</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(item, i) in accountTitles" :key="i">
              <td>{{ item.name }}</td>
              <td>{{ getIncomebyAccountTitle(item.titleId) }}</td>
              <td>{{ getOutgobyAccountTitle(item.titleId) }}</td>
            </tr>
          </tbody>
        </template>
      </v-table>
      <v-divider />
    </div>

    <!-- cashbook details -->
    <div class="pt-6 font">
      <h5>Ledger</h5>
      <v-table class="pt-1" density="compact">
        <template v-slot:default>
          <thead>
            <tr>
              <th>No.</th>
              <th>Date</th>
              <th>Account</th>
              <th>Description</th>
              <th>Supplier</th>
              <th>Income</th>
              <th>Expense</th>
              <th>Balance</th>
              <th>Tax</th>
              <th>Notes</th>
            </tr>
          </thead>
          <tbody v-if="cashbook">
            <!-- 引出 -->
            <tr>
              <td>Withdrawal</td>
              <td v-for="i in 4" :key="i">-</td>
              <td>{{ cashbook.carriedAmount }}</td>
              <td>-</td>
              <td>{{ cashbook.carriedAmount }}</td>
              <td v-for="i in 2" :key="i + 4">-</td>
            </tr>
            <!-- values -->
            <tr v-for="(item, i) in cashbook.details" :key="i">
              <td>{{ i + 1 }}</td>
              <td>{{ formatMonthDay(item.date) }}</td>
              <td>{{ item.title ? item.title.name : "-" }}</td>
              <td>{{ item.description || "-" }}</td>
              <td>{{ item.supplier || "-" }}</td>
              <td>{{ item.incomeAmount }}</td>
              <td>{{ item.outgoAmount }}</td>
              <td>{{ item.incomeAmount - item.outgoAmount }}</td>
              <td>
                {{
                  item.taxation
                    ? taxationOptions.find((t) => t.value == item.taxation).text
                    : "-"
                }}
              </td>
              <td>{{ item.note || "-" }}</td>
            </tr>
            <!-- total -->
            <tr>
              <td>Total</td>
              <td v-for="i in 4" :key="i">-</td>
              <td>{{ cashbook.totalIncomeAmount }}</td>
              <td>{{ cashbook.totalOutgoAmount }}</td>
              <td>{{ cashbook.remainingAmount }}</td>
              <td v-for="i in 2" :key="i + 4">-</td>
            </tr>
          </tbody>
        </template>
      </v-table>
      <v-divider />
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import LocalDataService from "@/service/LocalDataService";
import { cashbookModule } from "@/store/dataModules/cashbookModule";
import { AccountTitleResponse, CashbookResponse } from "chaild-api/lib";
import dayjs from "dayjs";
import { taxationOptions } from "@/constants/cashbook";
import { meModule } from "@/store/dataModules/meModule";
import ApiCashBook from "@/api/ApiCashBook";

export default defineComponent({
  name: "CashBookTable",

  async mounted() {
    if (this.$route.params.yearMonth) {
      this.yearMonth = this.$route.params.yearMonth as string;
      await this.getMe();
      await this.listCashbook();
      await this.listAccountTitle();

      await this.getPresignedUrl();
    }
  },

  data() {
    return {
      loading: false,
      yearMonth: "",
      taxationOptions: taxationOptions,
    };
  },

  computed: {
    cashbook: function (): CashbookResponse | null {
      return cashbookModule.cashbook;
    },
    accountTitles: function (): AccountTitleResponse[] {
      return cashbookModule.accountTitleList;
    },
    nurseryName: function (): string {
      const nurseryId = LocalDataService.getNurseryId();
      if (meModule.me && nurseryId) {
        return (
          meModule.me.nurserySchools.find(
            (n) => String(n.nurseryId) == String(nurseryId)
          )?.nurseryName || "Nursery"
        );
      } else {
        return "Nursery";
      }
    },
  },

  methods: {
    async listCashbook() {
      this.loading = true;
      const nurseryId = LocalDataService.getNurseryId();
      if (nurseryId) {
        await cashbookModule
          .listCashbook({
            nurseryId,
            yearMonth: this.yearMonth,
          })
          .catch();
      }
      this.loading = false;
    },
    async listAccountTitle() {
      this.loading = true;
      const nurseryId = LocalDataService.getNurseryId();
      if (nurseryId) {
        await cashbookModule.listAccountTitle({
          nurseryId,
        });
      }
      this.loading = false;
    },
    async getMe() {
      await meModule.fetchMe();
    },
    async getPresignedUrl() {
      // get base64
      const rawHtml = document.documentElement.outerHTML;
      const additionalStyle = `<style type="text/css">@import url(https://fonts.googleapis.com/css?family=Noto+Sans+JP);
        .font {font-family: "Noto Sans JP", sans-serif, system-ui !important;}
        body {font-family: "Noto Sans JP", sans-serif, system-ui !important;}
        div, table {font-family: "Noto Sans JP", sans-serif, system-ui !important;}
        </style>`;
      const headTagIndex = rawHtml.indexOf("<head>");
      const styleInserted =
        rawHtml.slice(0, headTagIndex + "<head>".length) +
        additionalStyle +
        rawHtml.slice(headTagIndex + "<head>".length, rawHtml.length);

      const encoded = window.btoa(unescape(encodeURIComponent(styleInserted)));
      // post
      const nurseryId = LocalDataService.getNurseryId();
      if (nurseryId && this.yearMonth) {
        const response = await ApiCashBook.getPresignedUrl({
          nurseryId,
          yearMonth: this.yearMonth,
          base64Html: encoded,
        }).catch();
        if (response) {
          window.open(response.preSignedDownloadUrl, "_blank");
        }
      }
    },
    getIncomebyAccountTitle(titleId: number): number {
      let total = 0;
      if (this.cashbook) {
        for (const amount of this.cashbook.details) {
          if (amount.title && amount.title.titleId == titleId) {
            total += amount.incomeAmount;
          }
        }
      }
      return total;
    },
    getOutgobyAccountTitle(titleId: number): number {
      let total = 0;
      if (this.cashbook) {
        for (const amount of this.cashbook.details) {
          if (amount.title && amount.title.titleId == titleId) {
            total += amount.outgoAmount;
          }
        }
      }
      return total;
    },
    formatMonthDay(value: string): string {
      if (!value) {
        return "";
      }
      return dayjs(value).format("MM-DD");
    },
  },
});
</script>
<style scoped>
/* @import url(https://fonts.googleapis.com/earlyaccess/notosansjp.css); */
@import url(https://fonts.googleapis.com/css?family=Noto+Sans+JP);

/* body {
  font-family: 'Noto Sans JP', sans-serif !important;
}
div, table {
  font-family: 'Noto Sans JP', sans-serif !important;
} */
.font {
  font-family: "Noto Sans JP", sans-serif, system-ui !important;
}
</style>
